import { supabase } from '../lib/supabase'
import {
  isSupportCenterFormKind,
  type SupportCenterFormKind,
} from '../types/supportCenterSpecialties'
import type {
  SupportCenterDemandType,
  SupportCenterReferralOrigin,
  SupportCenterTriageCatalogInput,
} from '../types/supportCenterTriage'
import { nowTimestampSaoPaulo } from '../utils/dateOnly'

const VALID_FORM_KINDS: SupportCenterFormKind[] = [
  'psicologico',
  'nutricional',
  'fitness',
  'juridico',
  'social',
]

function asFormKinds(raw: unknown): SupportCenterFormKind[] {
  if (!Array.isArray(raw)) return []
  const kinds = new Set<SupportCenterFormKind>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const kind = (item as { form_kind?: string }).form_kind
    if (isSupportCenterFormKind(kind)) {
      kinds.add(kind)
    }
  }
  return VALID_FORM_KINDS.filter((k) => kinds.has(k))
}

/** A-Z (pt-BR); "Outros" sempre por último */
function sortCatalogByName<T extends { name: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const aOutros = a.name.trim().toLocaleLowerCase('pt-BR') === 'outros'
    const bOutros = b.name.trim().toLocaleLowerCase('pt-BR') === 'outros'
    if (aOutros !== bOutros) return aOutros ? 1 : -1
    return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  })
}

function mapReferral(row: Record<string, unknown>): SupportCenterReferralOrigin {
  return {
    id: String(row.id),
    name: String(row.name ?? ''),
    requires_notes: Boolean(row.requires_notes),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
    deleted_at: (row.deleted_at as string | null) ?? null,
    deleted_by: (row.deleted_by as string | null) ?? null,
    deleted_reason: (row.deleted_reason as string | null) ?? null,
  }
}

function mapDemand(row: Record<string, unknown>): SupportCenterDemandType {
  return {
    id: String(row.id),
    name: String(row.name ?? ''),
    requires_notes: Boolean(row.requires_notes),
    form_kinds: asFormKinds(row.form_kinds),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
    deleted_at: (row.deleted_at as string | null) ?? null,
    deleted_by: (row.deleted_by as string | null) ?? null,
    deleted_reason: (row.deleted_reason as string | null) ?? null,
  }
}

async function syncDemandFormKinds(
  demandTypeId: string,
  formKinds: SupportCenterFormKind[]
): Promise<void> {
  const unique = Array.from(new Set(formKinds))
  if (unique.length === 0) {
    throw new Error('Selecione ao menos um formulário para exibir a demanda.')
  }

  const { error: deleteError } = await supabase
    .from('support_center_demand_type_form_kinds')
    .delete()
    .eq('demand_type_id', demandTypeId)

  if (deleteError) throw deleteError

  const { error: insertError } = await supabase
    .from('support_center_demand_type_form_kinds')
    .insert(unique.map((form_kind) => ({ demand_type_id: demandTypeId, form_kind })))

  if (insertError) throw insertError
}

export const supportCenterTriageCatalogService = {
  async listReferralOrigins(): Promise<SupportCenterReferralOrigin[]> {
    const { data, error } = await supabase
      .from('support_center_referral_origins')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (error) throw error
    return sortCatalogByName((data ?? []).map((row) => mapReferral(row as Record<string, unknown>)))
  },

  async listDemandTypes(): Promise<SupportCenterDemandType[]> {
    const { data, error } = await supabase
      .from('support_center_demand_types')
      .select(
        `
        *,
        form_kinds:support_center_demand_type_form_kinds(form_kind)
      `
      )
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (error) throw error
    return sortCatalogByName((data ?? []).map((row) => mapDemand(row as Record<string, unknown>)))
  },

  async upsertReferralOrigin(input: SupportCenterTriageCatalogInput): Promise<string> {
    const name = input.name.trim()
    if (!name) throw new Error('Informe o nome da opção.')

    const payload = {
      name,
      requires_notes: Boolean(input.requires_notes),
    }

    if (input.id) {
      const { error } = await supabase
        .from('support_center_referral_origins')
        .update(payload)
        .eq('id', input.id)
        .is('deleted_at', null)
      if (error) throw error
      return input.id
    }

    const { data, error } = await supabase
      .from('support_center_referral_origins')
      .insert(payload)
      .select('id')
      .single()
    if (error) throw error
    return String(data.id)
  },

  async upsertDemandType(input: SupportCenterTriageCatalogInput): Promise<string> {
    const name = input.name.trim()
    if (!name) throw new Error('Informe o nome da opção.')
    const formKinds = input.form_kinds ?? []
    if (formKinds.length === 0) {
      throw new Error('Selecione ao menos um formulário para exibir a demanda.')
    }

    const payload = {
      name,
      requires_notes: Boolean(input.requires_notes),
    }

    let demandTypeId = input.id
    if (demandTypeId) {
      const { error } = await supabase
        .from('support_center_demand_types')
        .update(payload)
        .eq('id', demandTypeId)
        .is('deleted_at', null)
      if (error) throw error
    } else {
      const { data, error } = await supabase
        .from('support_center_demand_types')
        .insert(payload)
        .select('id')
        .single()
      if (error) throw error
      demandTypeId = String(data.id)
    }

    await syncDemandFormKinds(demandTypeId, formKinds)
    return String(demandTypeId)
  },

  async softDeleteReferralOrigin(id: string, actorId: string, reason: string): Promise<void> {
    const deleted_reason = reason.trim()
    if (!deleted_reason) throw new Error('Informe o motivo da exclusão.')

    const { error } = await supabase
      .from('support_center_referral_origins')
      .update({
        deleted_at: nowTimestampSaoPaulo(),
        deleted_by: actorId,
        deleted_reason,
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  async softDeleteDemandType(id: string, actorId: string, reason: string): Promise<void> {
    const deleted_reason = reason.trim()
    if (!deleted_reason) throw new Error('Informe o motivo da exclusão.')

    const { error } = await supabase
      .from('support_center_demand_types')
      .update({
        deleted_at: nowTimestampSaoPaulo(),
        deleted_by: actorId,
        deleted_reason,
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },
}
